/**
 * RunTimer — how long the current question has been running (⏳ + duration).
 *
 * ── RE-HOMED ONTO THE PROTOCOL ──────────────────────────────────────────────────────────────────
 *
 * This read `multiAgentStore.runStartedAt` / `runFinishedAt` / `status` — three fields only the
 * LEGACY run lifecycle ever wrote. Nothing writes them on this surface any more, so the component
 * left as it was would have rendered `null` forever: present in the tree, invisible in the product.
 *
 * It now reads `RunState.run.started_at` / `finished_at` / `status`, which the backend stamps and
 * the protocol stream keeps current. Same fact, same display, a source that is actually fed.
 *
 * ── WHY IT EARNS ITS PLACE, HAVING ONCE BEEN REMOVED ────────────────────────────────────────────
 *
 * It was dropped from the header on the argument that the conversation already narrates the run.
 * The conversation says WHAT is happening; it does not say HOW LONG, and "is this run stuck or just
 * slow" is the question a reader actually has thirty seconds in — the one thing the narration
 * cannot answer. So the clock is back, and the earlier removal is the thing being corrected.
 *
 * What is NOT restored is a clock that ticks forever: `started_at` is null until a run begins, and
 * the component renders nothing then, so an idle screen carries no chrome.
 */
import { useEffect, useState } from "react";
import { Hourglass } from "lucide-react";
import { selectRun, useRunStateStore } from "@/stores/runStateStore";
import type { RunState } from "@/services/multi-agent/protocol";

function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

/** Terminal per the contract's `RunStatus`. Anything else is still in flight. */
const FINISHED = new Set(["completed", "failed", "cancelled", "expired"]);

/**
 * What the clock should say, as a pure function of the run — and, as often, what it should NOT say.
 *
 * Extracted from the component because the component cannot be tested: `renderToStaticMarkup` is
 * this repo's only rendering assertion (§12) and zustand serves a hook its INITIAL state under SSR,
 * so a store-reading component renders its empty case no matter what is seeded. The decisions worth
 * guarding are all here, and `RunTimer.test.ts` drives them directly.
 *
 * `null` means render nothing. `running` is carried out so the caller can decide to tick and to
 * colour, without re-deriving it.
 */
export function runElapsed(
  run: Pick<RunState["run"], "status" | "started_at" | "finished_at"> | null,
  now: number,
): { label: string; running: boolean } | null {
  const startedAt = run?.started_at ? Date.parse(run.started_at) : null;
  if (run == null || startedAt == null || Number.isNaN(startedAt)) return null;

  /*
   * `finished_at` is the authority and the status is the fallback, not the other way round. A run
   * can report `completed` a frame before its timestamp lands, and freezing on the status alone
   * would stop the clock at a time the backend had not yet stamped.
   */
  const parsed = run.finished_at ? Date.parse(run.finished_at) : null;
  const ended = parsed != null && !Number.isNaN(parsed) ? parsed : null;
  const running = ended == null && !FINISHED.has(run.status);

  /*
   * A FINISHED RUN WITH NO `finished_at` REPORTS NOTHING, and this guard is the whole reason this
   * is not simply `now - started_at`.
   *
   * Restoring yesterday's conversation replays a snapshot whose run is `completed` and whose
   * `finished_at` the backend may not carry. Counting to `now` there renders "1440m 12s" — a
   * fabricated duration, and a confident one. The run's real length is unknown; §1 says show
   * nothing rather than a plausible number.
   */
  if (!running && ended == null) return null;

  return { label: fmtDuration((ended ?? now) - startedAt), running };
}

export function RunTimer() {
  const run = useRunStateStore(selectRun);
  const elapsed = runElapsed(run, Date.now());
  const active = elapsed?.running === true;

  const [, force] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => force((x) => x + 1), 500);
    return () => clearInterval(id);
  }, [active]);

  if (!elapsed) return null;

  return (
    <span
      className="ma-micro inline-flex shrink-0 items-center gap-1"
      data-testid="run-elapsed"
      style={{
        textTransform: "none",
        letterSpacing: 0,
        color: active ? "var(--ma-accent)" : "var(--ma-text-muted)",
        fontVariantNumeric: "tabular-nums",
      }}
      title={active ? "Time elapsed for this question" : "Time this question took"}
    >
      <Hourglass aria-hidden className={`h-3 w-3 ${active ? "animate-pulse motion-reduce:animate-none" : ""}`} />
      {elapsed.label}
    </span>
  );
}
